import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/layout/AdminLayout";
import { api } from "../../lib/api";
import { Users, ListChecks, Wallet, TrendingUp, ArrowDownToLine, ArrowUpFromLine, UserPlus, Radio, Calendar, Bell, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";

const stats = [
  { key: "total_users", label: "Total Users", icon: Users, color: "bg-blue-50 text-blue-700" },
  { key: "total_bids", label: "Total Bids", icon: ListChecks, color: "bg-violet-50 text-violet-700" },
  { key: "live_balance", label: "Live Wallet Balance", icon: Wallet, color: "bg-emerald-50 text-emerald-700" },
  { key: "today_collection", label: "Today's Collection", icon: TrendingUp, color: "bg-amber-50 text-amber-700" },
  { key: "pending_withdrawals", label: "Pending Withdrawals", icon: ArrowUpFromLine, color: "bg-rose-50 text-rose-700" },
  { key: "pending_deposits", label: "Pending Deposits", icon: ArrowDownToLine, color: "bg-orange-50 text-orange-700" },
];

const AVATAR_COLORS = [
  "from-pink-500 to-rose-600",
  "from-amber-500 to-orange-600",
  "from-purple-500 to-indigo-600",
  "from-sky-500 to-cyan-600",
  "from-emerald-500 to-teal-600",
  "from-fuchsia-500 to-rose-600",
];

const todayIST = () => {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
};

export default function AdminDashboard() {
  const [data, setData] = useState({});
  const [daily, setDaily] = useState({ count: 0, users: [] });
  const [online, setOnline] = useState({ count: 0, users: [] });
  const [depDate, setDepDate] = useState(todayIST());
  const [deposits, setDeposits] = useState({ deposits: [], count: 0, total_approved: 0 });
  const [activePanel, setActivePanel] = useState("live"); // live | datewise | last24

  useEffect(() => {
    api.get("/admin/dashboard").then(({data}) => setData(data));
    api.get("/daily-joined-users?limit=50").then(({ data }) => setDaily(data)).catch(() => {});
    const loadOnline = () => api.get("/admin/online-users?minutes=5").then(({ data }) => setOnline(data)).catch(() => {});
    loadOnline();
    const id = setInterval(loadOnline, 10000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get(`/admin/deposits/by-date?date=${depDate}`).then(({ data }) => setDeposits(data)).catch(() => {});
  }, [depDate]);

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Overview</div>
        <h1 className="font-display font-bold text-3xl tracking-tight text-slate-900 mt-1">Dashboard</h1>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition" data-testid={`stat-${s.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}><Icon className="w-5 h-5" /></div>
              </div>
              <div className="text-3xl font-display font-semibold tabular-nums text-slate-900">{data[s.key] ?? 0}</div>
              <div className="text-sm text-slate-500 mt-0.5">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Send Broadcast Push Notification */}
      <BroadcastCard />

      {/* ============== 3-way panel switcher (Live / Date-wise / Last 24h) ============== */}
      <div className="mb-4" data-testid="dashboard-panel-switcher">
        <div className="inline-flex w-full bg-slate-100 border border-slate-200 rounded-xl p-1 gap-1">
          {[
            { key: "live",     label: "Live Now",      count: online.count || 0,   color: "emerald" },
            { key: "datewise", label: "Date-wise",     count: deposits.count || 0, color: "amber" },
            { key: "last24",   label: "Last 24 Hours", count: daily.count || 0,    color: "teal" },
          ].map(tab => {
            const active = activePanel === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActivePanel(tab.key)}
                data-testid={`dashboard-tab-${tab.key}`}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold transition ${
                  active
                    ? tab.color === "emerald" ? "bg-white text-emerald-700 shadow-sm border border-emerald-200"
                      : tab.color === "amber" ? "bg-white text-amber-700 shadow-sm border border-amber-200"
                      : "bg-white text-teal-700 shadow-sm border border-teal-200"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <span>{tab.label}</span>
                <span className={`min-w-[22px] px-1.5 py-0.5 rounded-full text-[10px] font-black tabular-nums ${
                  active
                    ? tab.color === "emerald" ? "bg-emerald-100 text-emerald-700"
                      : tab.color === "amber" ? "bg-amber-100 text-amber-700"
                      : "bg-teal-100 text-teal-700"
                    : "bg-slate-200 text-slate-600"
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ============== Active panel content ============== */}
      <div className="mb-6">
        {activePanel === "live" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="online-users-card">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-green-50">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow">
                <Radio className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">Live Now</div>
                <h2 className="font-bold text-lg text-slate-900">Online Users</h2>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-display font-black text-emerald-600 tabular-nums leading-none" data-testid="online-count">{online.count || 0}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Active · 5 min</div>
            </div>
          </div>
          {(!online.users || online.users.length === 0) ? (
            <div className="p-8 text-center text-slate-400 text-sm">No users online right now</div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto" data-testid="online-users-list">
              {online.users.slice(0, 50).map((u, i) => (
                <li key={u.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50" data-testid={`online-user-${u.id}`}>
                  <div className="relative">
                    <div className={`w-9 h-9 rounded-full text-white font-bold flex items-center justify-center shadow bg-gradient-to-br ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>
                      {(u.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/admin/users/${u.id}`} className="font-bold text-slate-900 truncate hover:text-orange-600">{u.name}</Link>
                    <div className="text-xs text-slate-500">{u.mobile} · Wallet ₹{u.wallet_balance}</div>
                  </div>
                  <span className="text-[9px] font-black bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full uppercase tracking-widest shrink-0">Online</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}

        {activePanel === "datewise" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="deposits-by-date-card">
          <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow">
                  <ArrowDownToLine className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-amber-700 font-bold">Date-wise</div>
                  <h2 className="font-bold text-lg text-slate-900">Deposits</h2>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-display font-black text-amber-700 tabular-nums leading-none">₹{deposits.total_approved || 0}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{deposits.count} txns</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input type="date" value={depDate} onChange={(e) => setDepDate(e.target.value)} max={todayIST()}
                className="flex-1 border-2 border-amber-200 rounded-lg px-3 py-1.5 text-sm font-bold text-amber-900 bg-white focus:outline-none focus:border-orange-500" data-testid="dep-date-picker" />
            </div>
          </div>
          {(!deposits.deposits || deposits.deposits.length === 0) ? (
            <div className="p-8 text-center text-slate-400 text-sm">Is date pe koi deposit nahi</div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto" data-testid="deposits-list">
              {deposits.deposits.map((t) => (
                <li key={t.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50">
                  <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center shrink-0">
                    ₹
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">{t.user_name || "—"} · <span className="text-slate-500 font-mono text-xs">{t.user_mobile || ""}</span></div>
                    <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} · UTR: {t.utr || (t.method === "imb" ? "IMB" : "—")}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="tabular-nums font-black text-slate-900">₹{t.amount}</div>
                    <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${t.status === "approved" ? "bg-emerald-100 text-emerald-700" : t.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{t.status}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}

        {activePanel === "last24" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="daily-users-card">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow">
                <UserPlus className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">Last 24 Hours</div>
                <h2 className="font-bold text-lg text-slate-900">Recently Joined Users</h2>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-display font-black text-emerald-600 tabular-nums leading-none" data-testid="daily-users-count">{daily.count || 0}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">New Users</div>
            </div>
          </div>

          {(!daily.users || daily.users.length === 0) ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              No new signups in last 24 hours.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto" data-testid="daily-users-list">
              {daily.users.map((u, i) => (
                <li key={u.id || i} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50" data-testid={`daily-user-row-${i}`}>
                  <div className={`w-10 h-10 rounded-full text-white font-black flex items-center justify-center shadow bg-gradient-to-br ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>
                    {(u.name || "P").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">🎉 {u.name}</div>
                    <div className="text-xs text-slate-500">
                      Joined {u.joined_at ? new Date(u.joined_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : "recently"}
                    </div>
                  </div>
                  <span className="text-[9px] font-black bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full uppercase tracking-widest shrink-0">New</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
      </div>
      <Toaster richColors position="top-center" />
    </AdminLayout>
  );
}

function BroadcastCard() {
  const [title, setTitle] = useState("🎰 Reddy Club");
  const [text, setText]   = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    if (!text.trim()) return toast.error("Message body required");
    if (!window.confirm("Ye message sabhi users ko push notification me chala jayega. Bhejein?")) return;
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/notice/broadcast", { title, body: text });
      setLastResult(data);
      if (data.push?.errors && data.push.errors.length) {
        toast.warning("Notification saved but OneSignal returned: " + JSON.stringify(data.push.errors));
      } else if (data.push?.id) {
        toast.success("Broadcast sent to all subscribed users!");
      } else {
        toast.info("Notification saved (OneSignal env not set or no subscribers yet)");
      }
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-950 border-2 border-yellow-400/60 rounded-2xl p-5 mb-6 shadow-xl relative overflow-hidden" data-testid="broadcast-card">
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-yellow-400/20 rounded-full blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-600 text-blue-950 flex items-center justify-center shadow-lg">
            <Bell className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-yellow-300 font-black">OneSignal Push</div>
            <h2 className="font-display font-black text-xl text-yellow-100">Send Broadcast Notification</h2>
          </div>
        </div>
        <div className="grid gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-yellow-300 font-bold mb-1 block">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="🎰 Reddy Club — Special Offer"
              maxLength={60}
              className="w-full h-11 px-4 rounded-xl bg-white/95 text-blue-950 placeholder:text-slate-400 font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400"
              data-testid="broadcast-title"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-yellow-300 font-bold mb-1 block">Message (sabhi users ko jayega)</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Jaise: 'Aaj Desawar ka result ready hai! App khol ke check karo. 🏆'"
              maxLength={200}
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-white/95 text-blue-950 placeholder:text-slate-400 font-semibold focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none"
              data-testid="broadcast-body"
            />
            <div className="text-right text-[10px] text-yellow-200 mt-0.5">{text.length}/200</div>
          </div>
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            data-testid="broadcast-send-btn"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500 text-blue-950 font-black tracking-wider shadow-lg active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            {sending ? "SENDING..." : "SEND TO ALL USERS"}
          </button>
        </div>
        {lastResult && (
          <div className="mt-3 p-3 bg-white/10 border border-white/20 rounded-lg text-[11px] text-yellow-100" data-testid="broadcast-result">
            {lastResult.push?.id ? (
              <span>✅ Push sent · notification id: <span className="font-mono text-yellow-300">{lastResult.push.id.slice(0, 12)}...</span></span>
            ) : lastResult.push?.errors ? (
              <span>⚠️ {JSON.stringify(lastResult.push.errors)}</span>
            ) : (
              <span>ℹ️ Broadcast saved. OneSignal API key set nahi hai ya koi subscriber nahi hai.</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
